import React, { useState, useEffect } from 'react';
import { Search, Filter, AlertCircle, User, Award, CheckCircle, Clock, XCircle } from 'lucide-react';
import useCertificationManagementStore from '@/stores/AdminStore/CertificationManagementStore';
import './LearnerProgress.css';

const LearnerProgress: React.FC = () => {
  const {
    learnerProgress,
    learnerProgressPagination,
    certifications,
    loading,
    error,
    fetchLearnerProgress,
    fetchCertifications,
    clearError,
  } = useCertificationManagementStore();

  const [filters, setFilters] = useState({
    certificationId: null as number | null,
    status: 'all',
    search: '',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  // Fetch certifications for filter dropdown
  useEffect(() => {
    if (certifications.length === 0) {
      fetchCertifications(1, 100, 'all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch learner progress when filters or page change
  useEffect(() => {
    fetchLearnerProgress(
      filters.certificationId,
      filters.status === 'all' ? null : filters.status,
      filters.search || null,
      currentPage,
      limit
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.certificationId, filters.status, filters.search, currentPage]);

  const handleFilterChange = (field: string, value: any) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, search: value }));
    setCurrentPage(1);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="status-icon-completed" />;
      case 'in_progress':
        return <Clock size={16} className="status-icon-in-progress" />;
      case 'expired':
      case 'revoked':
        return <XCircle size={16} className="status-icon-expired" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'expired':
        return 'Expired';
      case 'revoked':
        return 'Revoked';
      default:
        return status;
    }
  };

  const getProgressBarColor = (percentage: number) => {
    if (percentage === 100) return 'progress-complete';
    if (percentage >= 75) return 'progress-high';
    if (percentage >= 50) return 'progress-medium';
    if (percentage >= 25) return 'progress-low';
    return 'progress-very-low';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="admin-certifi-learner-progress">
      {/* Error Alert */}
      {error && (
        <div className="admin-certifi-alert admin-certifi-alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={clearError}>×</button>
        </div>
      )}

      {/* Filters */}
      <div className="admin-certifi-progress-filters">
        <div className="admin-certifi-filter-group">
          <label htmlFor="certification-filter">
            <Filter size={16} />
            Certification
          </label>
          <select
            id="certification-filter"
            value={filters.certificationId || ''}
            onChange={(e) =>
              handleFilterChange('certificationId', e.target.value ? parseInt(e.target.value) : null)
            }
          >
            <option value="">All Certifications</option>
            {certifications.map((cert) => (
              <option key={cert.id} value={cert.id}>
                {cert.certification_name}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-certifi-filter-group">
          <label htmlFor="status-filter">
            <Filter size={16} />
            Status
          </label>
          <select
            id="status-filter"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>

        <div className="admin-certifi-filter-group admin-certifi-search-group">
          <label htmlFor="search-input">
            <Search size={16} />
            Search
          </label>
          <input
            id="search-input"
            type="text"
            placeholder="Search by learner name..."
            value={filters.search}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && learnerProgress.length === 0 ? (
        <div className="admin-certifi-loading">
          <div className="spinner"></div>
          <p>Loading learner progress...</p>
        </div>
      ) : learnerProgress.length === 0 ? (
        <div className="admin-certifi-empty-state">
          <User size={48} />
          <h3>No Learner Progress Found</h3>
          <p>No learners are currently enrolled in any certifications matching your filters.</p>
        </div>
      ) : (
        <>
          {/* Progress Table */}
          <div className="admin-certifi-progress-table-container">
            <table className="admin-certifi-progress-table">
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Certification</th>
                  <th>Enrolled Date</th>
                  <th>Progress</th>
                  <th>Courses</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {learnerProgress.map((entry) => (
                  <tr key={entry.enrollment_id}>
                    <td>
                      <div className="admin-certifi-student-info">
                        <div className="admin-certifi-student-avatar">
                          <User size={20} />
                        </div>
                        <div>
                          <div className="admin-certifi-student-name">{entry.student_name}</div>
                          <div className="admin-certifi-student-email">{entry.student_email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="admin-certifi-cert-name">
                        <Award size={16} />
                        {entry.certification_name}
                      </div>
                    </td>
                    <td>{formatDate(entry.enrollment_date)}</td>
                    <td>
                      <div className="admin-certifi-progress-cell">
                        <div className={`admin-certifi-progress-bar ${getProgressBarColor(entry.progress_percentage)}`}>
                          <div
                            className="admin-certifi-progress-fill"
                            style={{ width: `${entry.progress_percentage}%` }}
                          ></div>
                        </div>
                        <span className="admin-certifi-progress-text">{entry.progress_percentage}%</span>
                      </div>
                    </td>
                    <td>
                      <span className="admin-certifi-course-count">
                        {entry.completed_courses} / {entry.total_required_courses}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-certifi-status-badge admin-certifi-status-${entry.status}`}>
                        {getStatusIcon(entry.status)}
                        {getStatusText(entry.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {learnerProgressPagination && learnerProgressPagination.totalPages > 1 && (
            <div className="admin-certifi-pagination">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || loading}
                className="admin-certifi-pagination-btn"
              >
                Previous
              </button>
              <span className="admin-certifi-pagination-info">
                Page {currentPage} of {learnerProgressPagination.totalPages} ({learnerProgressPagination.total} total)
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(learnerProgressPagination.totalPages, prev + 1))}
                disabled={currentPage === learnerProgressPagination.totalPages || loading}
                className="admin-certifi-pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LearnerProgress;
